import React from 'react';
import FocusTrap from 'focus-trap-react';
import {
  Close as CloseIcon,
  Warning as WarningIcon,
  CheckCircle as ConfirmIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import './ConfirmationModal.css';

interface ConfirmationModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'warning',
  onConfirm,
  onCancel,
  isLoading = false
}) => {
  if (!isOpen) {
    return null;
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isLoading) {
      onCancel();
    }
  };

  const getIcon = () => {
    switch (variant) {
      case 'danger':
        return <WarningIcon className={`confirmation-icon ${variant}`} />;
      case 'warning':
        return <WarningIcon className={`confirmation-icon ${variant}`} />;
      case 'info':
        return <ConfirmIcon className={`confirmation-icon ${variant}`} />;
      default:
        return <WarningIcon className={`confirmation-icon ${variant}`} />;
    }
  };

  return (
    <FocusTrap
      focusTrapOptions={{
        allowOutsideClick: true,
        escapeDeactivates: true
      }}
    >
      <div className="modal-overlay" onClick={handleOverlayClick} role="presentation">
        <div 
          className="confirmation-modal-container"
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirmation-modal-title"
          aria-describedby="confirmation-modal-message"
        >
          <div className="confirmation-modal-header">
            <div className="confirmation-title-section">
              {getIcon()}
              <h2 id="confirmation-modal-title" className="confirmation-title">{title}</h2>
            </div>
            <button 
              className="modal-close-btn" 
              onClick={onCancel}
              disabled={isLoading}
              aria-label="Close dialog"
            >
              <CloseIcon aria-hidden="true" />
            </button>
          </div>

          <div className="confirmation-modal-body">
            <p id="confirmation-modal-message" className="confirmation-message">{message}</p>
          </div>

          <div className="confirmation-modal-actions">
            <button
              type="button"
              onClick={onCancel}
              className="btn btn-outline"
              disabled={isLoading}
              aria-label={cancelText}
            >
              <CancelIcon aria-hidden="true" />
              {cancelText}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className={`btn ${variant === 'danger' ? 'btn-danger' : 'btn-primary'}`}
              disabled={isLoading}
              aria-label={isLoading ? 'Processing' : confirmText}
            >
              <ConfirmIcon aria-hidden="true" />
              {isLoading ? 'Processing...' : confirmText}
            </button>
          </div>
        </div>
      </div>
    </FocusTrap>
  );
};

export default ConfirmationModal;
